import {
  useEffect,
  useRef,
  useState,
  type ReactNode
} from "react";
import { useAuth } from "../../auth/AuthProvider.js";
import { ApiFetchError } from "../../lib/api.js";
import {
  Badge,
  Button,
  FileUpload,
  Modal,
  ProductImage
} from "../../components/index.js";
import type { BoxContentSku, BoxContentError } from "../../lib/box-content.js";
import { mapBackendFieldErrors, validateBoxContent } from "../../lib/box-content.js";
import { isPackingInfoDone } from "../../lib/plan-status.js";

/**
 * Modal do grupo de embalagem (Design.md §6.2 + entrega esperada). Fluxo:
 * baixar template (.xlsx com caixas em branco) → preencher → subir → o FiCC
 * valida (mostrando onde corrigir, por SKU/caixa) → envia ao backend/Amazon →
 * trata erros → ao dar certo, libera o avanço.
 *
 * NB: `onSubmitted` é apenas um fast-path. A prontidão AUTORITATIVA do Passo 2
 * (habilitar "Avançar") é derivada do status real do plano em Step2Embalagem —
 * ver `isPackingInfoDone`. Assim o avanço não depende deste poll de 24s.
 */
const PRINT_FORMATS = ["Impressão térmica", "PDF A4"] as const;

type Phase =
  | "idle"
  | "errors"
  | "submitting"
  | "polling"
  | "processing"
  | "amazon-failed"
  | "done";

export interface GrupoEmbalagemModalProps {
  open: boolean;
  onClose: () => void;
  planId: string;
  packingGroupId: string;
  /** Rótulo do grupo ("Grupo de embalagem 2") — a opção pode ter N grupos. */
  groupLabel?: string;
  /**
   * `false` quando AINDA faltam outros grupos: o backend segue aguardando as
   * demais planilhas, então não faz sentido pollar até a Amazon aceitar o
   * packing information. Só o último grupo pendente espera a Amazon.
   */
  waitForAmazon?: boolean;
  skus: readonly BoxContentSku[];
  onSubmitted: (boxCount: number) => void;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function GrupoEmbalagemModal({
  open,
  onClose,
  planId,
  packingGroupId,
  groupLabel = "Grupo de embalagem 1",
  waitForAmazon = true,
  skus,
  onSubmitted
}: GrupoEmbalagemModalProps): ReactNode {
  const { api } = useAuth();
  const [boxCount, setBoxCount] = useState(1);
  const [printFormat, setPrintFormat] = useState<string>(PRINT_FORMATS[0]);
  const [fileName, setFileName] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const [errors, setErrors] = useState<BoxContentError[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  // Feedback DEDICADO da impressão de etiquetas FNSKU: o `message` do fluxo de
  // upload só aparece nas fases processing/done, então a impressão (disponível
  // já na fase idle) ficava sem retorno visível — clique sem feedback parecia
  // "não funcionar". Este estado é mostrado ao lado do botão em qualquer fase.
  const [labelMsg, setLabelMsg] = useState<string | null>(null);
  const [printing, setPrinting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const alive = useRef(true);
  const submittedBoxes = useRef(0);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const totalUnits = skus.reduce((a, s) => a + s.expectedQuantity, 0);

  async function baixarTemplate(): Promise<void> {
    setGenerating(true);
    setMessage(null);
    try {
      const { generateBoxContentWorkbook, downloadBlob } = await import(
        "../../lib/box-content-workbook.js"
      );
      const blob = await generateBoxContentWorkbook({
        packingGroupId,
        boxCount: Math.max(1, boxCount),
        skus
      });
      downloadBlob(blob, `grupo-embalagem-${packingGroupId}.xlsx`);
    } catch {
      setMessage("Não foi possível gerar o template. Tente novamente.");
    } finally {
      if (alive.current) {
        setGenerating(false);
      }
    }
  }

  async function subirPlanilha(file: File): Promise<void> {
    setFileName(file.name);
    setErrors([]);
    setMessage(null);
    setPhase("idle");
    // 1) Parse + validação client-side (mostra onde corrigir).
    const { parseBoxContentWorkbook } = await import(
      "../../lib/box-content-workbook.js"
    );
    const parsed = await parseBoxContentWorkbook(file);
    if (!parsed.ok) {
      setErrors([{ message: parsed.error }]);
      setPhase("errors");
      return;
    }
    // Valida contra os dados AUTORITATIVOS do plano (não as células editáveis).
    const { errors: vErrors, boxConfig } = validateBoxContent(parsed.value, {
      packingGroupId,
      expectedBySku: new Map(skus.map((s) => [s.sku, s.expectedQuantity]))
    });
    if (vErrors.length > 0 || boxConfig === null) {
      setErrors(vErrors);
      setPhase("errors");
      return;
    }

    // 2) Envia ao backend (validação Zod) → 3) evento → 4) Amazon (poll).
    submittedBoxes.current = boxConfig.boxes.length;
    setPhase("submitting");
    let boxConfigId: string;
    try {
      const res = await api.putBoxConfig(planId, boxConfig);
      boxConfigId = res.boxConfigId;
    } catch (error: unknown) {
      if (error instanceof ApiFetchError) {
        // Erros de campo do backend (Zod) → localizados por caixa.
        setErrors(
          error.fields !== null
            ? mapBackendFieldErrors(error.fields, boxConfig)
            : [{ message: error.message }]
        );
      } else {
        setErrors([{ message: "Falha ao enviar a configuração de caixas." }]);
      }
      setPhase("errors");
      return;
    }

    try {
      await api.submitBoxConfigEvent(planId, boxConfigId);
    } catch (error: unknown) {
      setErrors([
        {
          message:
            error instanceof Error
              ? error.message
              : "Falha ao enviar para processamento."
        }
      ]);
      setPhase("errors");
      return;
    }

    // Faltam outros grupos → o backend continua aguardando; confirma o envio
    // deste grupo sem pollar a aceitação da Amazon (que só ocorre no último).
    if (!waitForAmazon) {
      setPhase("done");
      setMessage(
        "✓ Caixas deste grupo registradas. Envie a planilha dos demais grupos para continuar."
      );
      onSubmitted(submittedBoxes.current);
      return;
    }

    await pollStatus();
  }

  async function pollStatus(): Promise<void> {
    setPhase("polling");
    for (let i = 0; i < 8; i += 1) {
      let status: Awaited<ReturnType<typeof api.getWorkflowStatus>>;
      try {
        status = await api.getWorkflowStatus(planId);
      } catch (error: unknown) {
        // Antes o erro de rede/sessão era engolido e virava "ainda processando"
        // — o operador ficava clicando em "Verificar status" sem saber que a
        // consulta é que estava falhando. Agora a causa aparece.
        if (alive.current) {
          setPhase("processing");
          setMessage(
            `Não foi possível consultar o status do plano: ${
              error instanceof Error ? error.message : "falha de rede"
            }. As caixas foram enviadas — clique em "Verificar status" para tentar de novo.`
          );
        }
        return;
      }
      if (!alive.current) {
        return;
      }
      if (status.workflowStatus === "FAILED") {
        // O motivo real (código Amazon + SKU) vem no status; sem guardá-lo aqui
        // o modal mostrava só o código interno "AMAZON_OPERATION_FAILED".
        if (
          typeof status.failureReason === "string" &&
          status.failureReason.length > 0
        ) {
          setMessage(status.failureReason);
        }
        setPhase("amazon-failed");
        return;
      }
      if (isPackingInfoDone(status.planStatus)) {
        setPhase("done");
        setMessage("✓ Conteúdo das caixas aceito pela Amazon. Você pode avançar.");
        onSubmitted(submittedBoxes.current);
        return;
      }
      await sleep(3000);
      if (!alive.current) {
        return;
      }
    }
    setPhase("processing");
    setMessage(
      "Enviado à Amazon. Ainda processando — clique em \"Verificar status\" em instantes."
    );
  }

  return (
    <Modal open={open} onClose={onClose} title={groupLabel} width={720}>
      <p style={{ fontSize: 12.5, color: "var(--text-muted)", marginBottom: "0.85rem" }}>
        Estes SKUs podem ser embalados juntos. {skus.length} SKUs · {totalUnits} unidades.
      </p>

      {/* Formato de impressão (Bristol 50×30 mm — §9.6) */}
      <div style={{ display: "flex", gap: "1rem", alignItems: "flex-end", flexWrap: "wrap", marginBottom: "0.85rem" }}>
        <Labeled label="Formato de impressão">
          <select value={printFormat} onChange={(e) => setPrintFormat(e.target.value)}>
            {PRINT_FORMATS.map((f) => (
              <option key={f}>{f}</option>
            ))}
          </select>
        </Labeled>
        <Labeled label="Largura (mm)">
          <input type="number" defaultValue={50} style={{ width: 80 }} readOnly />
        </Labeled>
        <Labeled label="Altura (mm)">
          <input type="number" defaultValue={30} style={{ width: 80 }} readOnly />
        </Labeled>
        <Button
          variant="secondary"
          disabled={printing}
          onClick={() => {
            setPrinting(true);
            setLabelMsg(null);
            void imprimirEtiquetas(api, planId, (m) => {
              if (alive.current) setLabelMsg(m);
            }).finally(() => {
              if (alive.current) setPrinting(false);
            });
          }}
        >
          {printing ? "Baixando…" : "Imprimir todas as etiquetas de SKU"}
        </Button>
      </div>
      {labelMsg !== null ? (
        <p role="status" style={{ fontSize: 12.5, color: "var(--text-muted)", marginBottom: "0.85rem" }}>
          {labelMsg}
        </p>
      ) : null}

      {/* Download / Upload da planilha (oculto após rejeição da Amazon ou sucesso). */}
      {phase !== "amazon-failed" && phase !== "done" ? (
        <div style={{ display: "flex", gap: "0.6rem", alignItems: "center", flexWrap: "wrap", marginBottom: "0.85rem" }}>
          <Labeled label="Nº de caixas">
            <input
              type="number"
              min={1}
              value={boxCount}
              onChange={(e) => setBoxCount(Math.max(1, Number(e.target.value) || 1))}
              style={{ width: 80 }}
            />
          </Labeled>
          <Button onClick={() => void baixarTemplate()} disabled={generating}>
            {generating ? "Gerando…" : "⬇ Baixar planilha do grupo (.xlsx)"}
          </Button>
          <FileUpload
            label="⬆ Subir planilha preenchida"
            accept=".xlsx"
            fileName={fileName}
            onFile={(file) => void subirPlanilha(file)}
          />
        </div>
      ) : null}

      {/* Estado do envio */}
      {phase === "submitting" ? <p role="status">Enviando configuração de caixas…</p> : null}
      {phase === "polling" ? <p role="status">Validando na Amazon…</p> : null}

      {phase === "errors" ? (
        <div
          role="alert"
          style={{
            border: "1px solid var(--orange)",
            background: "var(--orange-soft)",
            borderRadius: 9,
            padding: "0.75rem",
            marginBottom: "0.85rem"
          }}
        >
          <div style={{ fontWeight: 700, color: "var(--orange)", marginBottom: "0.4rem" }}>
            Corrija a planilha e suba novamente:
          </div>
          <ul style={{ margin: 0, paddingLeft: "1.1rem", fontSize: 12.5 }}>
            {errors.map((e, i) => (
              <li key={`${e.sku ?? ""}-${e.boxNumber ?? ""}-${i}`}>{e.message}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {phase === "amazon-failed" ? (
        <div role="alert" style={{ color: "var(--orange)", marginBottom: "0.85rem" }}>
          A Amazon rejeitou o conteúdo das caixas. Não é possível reenviar a
          planilha para o mesmo plano após a rejeição — feche este modal e use a
          opção de criar um novo envio no Passo 2 (SDD §9.8).
          {message !== null && message.length > 0 ? (
            <p style={{ margin: "0.5rem 0 0", fontSize: 12.5, color: "var(--text)" }}>
              <strong>Motivo:</strong> {message}
            </p>
          ) : null}
        </div>
      ) : null}

      {phase === "processing" ? (
        <div style={{ marginBottom: "0.85rem" }}>
          <p role="status" style={{ marginBottom: "0.4rem" }}>{message}</p>
          <Button variant="secondary" onClick={() => void pollStatus()}>
            Verificar status
          </Button>
        </div>
      ) : null}

      {phase === "done" ? (
        <div style={{ marginBottom: "0.85rem" }}>
          <Badge tone="green">{message}</Badge>
        </div>
      ) : null}

      {/* Tabela por SKU do grupo */}
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
        <thead>
          <tr>
            <th style={{ ...th, width: 44 }} />
            <th style={th}>Código SKU</th>
            <th style={th}>Informações / preparação</th>
            <th style={{ ...th, textAlign: "right" }}>Qtd</th>
          </tr>
        </thead>
        <tbody>
          {skus.map((sku) => (
            <tr key={sku.sku} className="ficc-hover-row">
              <td style={td}>
                <ProductImage src={sku.imageUrl ?? ""} label={sku.title || sku.sku} size={34} />
              </td>
              <td style={td}>
                <div style={{ fontWeight: 600 }}>{sku.title || sku.sku}</div>
                <div className="mono" style={{ fontSize: 11, color: "var(--text-muted)" }}>
                  {sku.sku} · {sku.asin}
                </div>
              </td>
              <td style={td}>{sku.prepType || "Sem preparação adicional"}</td>
              <td style={{ ...td, textAlign: "right", fontWeight: 700 }}>
                {sku.expectedQuantity}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Modal>
  );
}

async function imprimirEtiquetas(
  api: ReturnType<typeof useAuth>["api"],
  planId: string,
  setMessage: (m: string) => void
): Promise<void> {
  try {
    const { downloadBlob } = await import("../../lib/box-content-workbook.js");
    let blob: Blob;
    try {
      blob = await api.downloadLabel(planId, "fnsku");
    } catch (error) {
      // 404 = PDF ainda não gerado: gera (lazy, createMarketplaceItemLabels) e
      // re-baixa. O backend grava o PDF no GCS e devolve os bytes.
      if (error instanceof ApiFetchError && error.status === 404) {
        setMessage("Gerando etiquetas FNSKU…");
        await api.generateFnskuLabels(planId);
        // Re-baixa com pequenas re-tentativas: o generate persiste o path antes
        // de responder, mas evita qualquer corrida de 404 (consistência/latência)
        // que fazia o operador ver "etiqueta indisponível" logo após gerar.
        blob = await downloadFnskuWithRetry(api, planId);
      } else {
        throw error;
      }
    }
    downloadBlob(blob, `etiquetas-sku-${planId}.pdf`);
    setMessage("✓ Etiquetas FNSKU baixadas (PDF).");
  } catch (error) {
    // Falha NUNCA é silenciosa: o erro real (status/código/mensagem) é exibido
    // — antes tudo virava uma linha cinza genérica, e o usuário lia como "nada
    // acontece" mesmo quando o backend reportava a causa.
    setMessage(fnskuErrorMessage(error));
  }
}

/**
 * Baixa o PDF de etiquetas FNSKU com re-tentativas curtas em 404. Chamada logo
 * após `generateFnskuLabels` (que persiste o path antes de responder 201) — as
 * re-tentativas só blindam contra qualquer corrida/latência residual em que o
 * download corre antes do path ficar visível.
 */
async function downloadFnskuWithRetry(
  api: ReturnType<typeof useAuth>["api"],
  planId: string,
  attempts = 4
): Promise<Blob> {
  let lastError: unknown = null;
  for (let i = 0; i < attempts; i += 1) {
    try {
      return await api.downloadLabel(planId, "fnsku");
    } catch (error) {
      lastError = error;
      // Só re-tenta o 404 transitório; outros erros propagam de imediato.
      if (!(error instanceof ApiFetchError && error.status === 404)) {
        throw error;
      }
      if (i < attempts - 1) {
        await sleep(800);
      }
    }
  }
  throw lastError;
}

/** Mensagem pt-BR específica para a falha de impressão das etiquetas FNSKU. */
function fnskuErrorMessage(error: unknown): string {
  if (error instanceof ApiFetchError) {
    if (error.code === "FNSKU_LABELS_NOT_APPLICABLE") {
      return "Etiquetas FNSKU não se aplicam: nenhum item com etiquetagem pelo vendedor (labelOwner = AMAZON).";
    }
    if (error.status === 401 || error.status === 403) {
      return "Sem permissão para gerar/baixar as etiquetas FNSKU (faça login como Admin/Operador).";
    }
    return `Falha ao baixar as etiquetas FNSKU (HTTP ${error.status}${
      error.code.length > 0 ? ` · ${error.code}` : ""
    }): ${error.message}`;
  }
  return error instanceof Error
    ? `Falha ao baixar as etiquetas FNSKU: ${error.message}`
    : "Falha ao baixar as etiquetas FNSKU.";
}

function Labeled({ label, children }: { label: string; children: ReactNode }): ReactNode {
  // <label> envolvendo o controle = associação implícita (acessível sem id).
  return (
    <label style={{ display: "inline-block" }}>
      <div style={{ fontSize: 10.5, color: "var(--text-muted)", marginBottom: 2 }}>{label}</div>
      {children}
    </label>
  );
}

const th = {
  textAlign: "left",
  background: "var(--panel-alt)",
  color: "var(--text-muted)",
  fontSize: 10.5,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  padding: "0.6rem 0.7rem",
  borderBottom: "1px solid var(--border)"
} as const;

const td = {
  padding: "0.6rem 0.7rem",
  borderBottom: "1px solid var(--border-soft)",
  verticalAlign: "top"
} as const;
